import React, { useRef } from 'react';
import 'twin.macro';
import { Field, Form } from 'react-final-form';
import { Eleve } from 'types/eleves';
import Input from 'components/Form/InputGroup/formWrapper';
import { useTranslatedValidation } from 'utils/useTranslatedValidation';
import useDeleteEleve from 'queries/eleves/useDeleteEleve';
import { useTranslation } from 'react-i18next';
import ButtonRadio from 'components/Form/ButtonsRadio/formWrapper';
import DateInput from 'components/Form/Dateinput2';
import { useUploadElevePhoto } from 'queries/eleves/useUploadElevePhoto';
import { AvatarCard } from 'components/AvatarCard';
import { CardLayout } from 'containers/Eleves/EleveDetails/Main/components/CardLayout';
import { ClassroomsSelector } from 'containers/Eleves/List/components/ClassroomsSelector';
import { PlusIcon as Plus, TrashIcon } from '@heroicons/react/24/outline';
import useClassrooms from 'containers/App/context/ClassroomsContext/useClassrooms';
import FormGroup from 'components/Form/FormGroup';
import Editor from 'components/Editor/formWrapper';
import { NewCustomFields } from 'components/NewCustomFields';
import _isEmpty from 'lodash-es/isEmpty';
import useElevesCustomFields from 'queries/eleves/customFields/useElevesCustomFields';
import useUpdateElevesCustomFields from 'queries/eleves/customFields/useUpdateElevesCustomFields';
import Popover from 'components/Popover';
import { AddNewFieldPopoverWrapper } from 'containers/Eleves/List/components/AddNewField';
import DeleteButton from 'components/Buttons/DeleteButton';
import AutoSubmitForm from 'components/AutoSave';
import { useLocation, useOutletContext } from 'react-router-dom';
import useUpdateEleve from 'queries/eleves/useUpdateEleve';
import { Decloisonnement } from './Decloisonnement';
import { UserLocale } from '@teetsh/app/src/types/user';
import { EleveGroupSelector } from 'components/EleveGroupSelector';

export function EleveForm() {
  const { eleve } = useOutletContext<{
    eleve: Eleve;
  }>();
  const updateEleve = useUpdateEleve();
  const { required, hasXcharacters } = useTranslatedValidation();
  const location = useLocation();
  const viewId =
    new URLSearchParams(location.search).get('viewId') || undefined;
  const deleteEleve = useDeleteEleve();
  const { data: cFields = [] } = useElevesCustomFields();
  const updateCustomFields = useUpdateElevesCustomFields();
  const classrooms = useClassrooms();
  const buttonRef = useRef();
  const { upload: uploadElevePhoto } = useUploadElevePhoto();
  const { t } = useTranslation(['eleve', 'common']);

  return (
    <Form
      initialValues={eleve}
      // le then c'est pour pas que final-form considère que c'est une erreur
      keepDirtyOnReinitialize
      onSubmit={v => updateEleve(v).then(() => ({}))}
      render={({ handleSubmit, values }) => (
        <form onSubmit={handleSubmit}>
          <AutoSubmitForm />
          <div tw="space-y-10">
            <CardLayout
              title={t('eleve:informationsGeneralesSurEleve')}
              description={t('eleve:informationsGeneralesDescription')}
            >
              <div tw="col-span-full">
                <div tw="flex gap-x-6">
                  <div>
                    <div tw="mt-2 flex items-center gap-x-3">
                      <Field<string>
                        name="picture"
                        render={({ input: { onChange, value } }) => (
                          <AvatarCard
                            large
                            onUpload={uploadElevePhoto}
                            onChange={v => onChange(v ? v : 'user')}
                            picture={value === 'user' ? undefined : value}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div tw="flex-grow max-w-xs">
                    <Field
                      id="givenName"
                      validate={required}
                      name="givenName"
                      label={`${t('eleve:fields.givenName')}*`}
                      autoComplete="off"
                      component={Input}
                    />

                    <Field
                      id="familyName"
                      validate={required}
                      name="familyName"
                      label={`${t('eleve:fields.familyName')}*`}
                      autoComplete="off"
                      component={Input}
                    />
                  </div>
                </div>
              </div>
              <div tw="col-span-full">
                <div tw="flex gap-x-6">
                  <Field
                    validate={required}
                    autoFocus
                    name="gender"
                    label={`${t('eleve:fields.gender')}*`}
                    options={[
                      {
                        label: t('eleve:fields.boy'),
                        value: 'H',
                      },
                      {
                        label: t('eleve:fields.girl'),
                        value: 'F',
                      },
                    ]}
                    component={ButtonRadio}
                  />
                </div>
              </div>
              <div tw="col-span-full max-w-xs">
                <Field
                  id="birthday"
                  name="birthday"
                  render={({ input: { value, onChange } }) => (
                    <DateInput
                      userLocale={t('common:locale.long') as UserLocale}
                      label={t('eleve:fields.birthday')}
                      canBeNull
                      id="birthday"
                      value={value === '' ? null : value}
                      onChange={v => {
                        if (v !== undefined) {
                          onChange(v);
                        }
                      }}
                    />
                  )}
                />
              </div>

              <div tw="col-span-full max-w-xs">
                <Field
                  name="ine"
                  label={t('eleve:fields.ine')}
                  id="ine"
                  error={hasXcharacters(11)(values.ine)}
                  type="text"
                  helperText={t('eleve:fields.ineHelperText')}
                  parse={v => v || ''} // to prevent an infinite loop when emptying the field
                  component={Input}
                />
              </div>
              <div tw="col-span-full">
                <FormGroup label={t('eleve:fields.remark')}>
                  <Field
                    name="comment"
                    minHeight={100}
                    label={t('eleve:fields.remark')}
                    inline
                    component={Editor}
                  />
                </FormGroup>
              </div>
            </CardLayout>

            <CardLayout
              title={t('eleve:configurationDeLaClasse')}
              description={t('eleve:configurationDeLaClasseDescription')}
            >
              <div tw="col-span-full max-w-xs">
                <Field
                  name="classroomId"
                  render={p => (
                    <ClassroomsSelector
                      {...p}
                      label={t('eleve:fields.classroom')}
                      options={classrooms.map(cl => ({
                        label: cl.name,
                        value: cl.id,
                      }))}
                    />
                  )}
                />
              </div>
              <div tw="col-span-full max-w-xs">
                <Field
                  name="groups"
                  render={({ input: { onChange, value = [] } }) => (
                    <FormGroup
                      label={t('eleve:fields.groups')}
                      withoutMargin
                      helperText={t('eleve:fields.groupHelperText')}
                    >
                      <EleveGroupSelector value={value} onChange={onChange} />
                    </FormGroup>
                  )}
                />
              </div>
              <div tw="sm:col-span-full">
                <Field
                  id="decloisonement"
                  name="decloisonement"
                  allowNull
                  parse={value => value}
                  render={({ input: { onChange, value } }) => (
                    <div tw="max-w-[16rem]">
                      <Decloisonnement value={value} onChange={onChange} />
                    </div>
                  )}
                />
              </div>

              <div tw="sm:col-span-3">
                <Field
                  id="arrivalDate"
                  name="arrivalDate"
                  render={({ input: { value, onChange } }) => (
                    <DateInput
                      userLocale={t('common:locale.long') as UserLocale}
                      label={t('eleve:fields.arrivalDate')}
                      canBeNull
                      id="arrivalDate"
                      value={value === '' ? null : value}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
              <div tw="sm:col-span-3">
                <Field
                  id="radiationDate"
                  name="radiationDate"
                  render={({ input: { value, onChange } }) => (
                    <DateInput
                      userLocale={t('common:locale.long') as UserLocale}
                      label={t('eleve:fields.radiationDate')}
                      canBeNull
                      id="radiationDate"
                      value={value === '' ? null : value}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
            </CardLayout>
            <CardLayout
              title={t('eleve:customFields')}
              description={t('eleve:customFieldsDescription')}
            >
              <div tw="col-span-full">
                <Field
                  name="customFields"
                  render={({ input: { value, onChange } }) => {
                    if (cFields.length === 0) {
                      return null;
                    }
                    return (
                      <div tw="space-y-8 mb-8">
                        {cFields.map(cField => {
                          const matchingField = (value || []).find(
                            v => v.id === cField.id,
                          );
                          return (
                            <NewCustomFields
                              withoutBorders
                              key={cField.id}
                              customFieldDefinition={cField}
                              value={matchingField?.value}
                              options={(cField.options || []).map(opt => ({
                                value: opt.value,
                                id: opt.id,
                                color: opt.color,
                              }))}
                              onUpdateCustomField={updateCustomFields}
                              onChange={v => {
                                if (matchingField === undefined) {
                                  return onChange([
                                    ...value,
                                    {
                                      id: cField.id,
                                      value: v,
                                    },
                                  ]);
                                }
                                if (
                                  v === undefined ||
                                  (cField.type !== 'number' && _isEmpty(v))
                                ) {
                                  return onChange(
                                    value.filter(
                                      p => matchingField?.id !== p.id,
                                    ),
                                  );
                                }
                                return onChange(
                                  value.map(p => {
                                    if (matchingField?.id === p.id) {
                                      return {
                                        id: p.id,
                                        value: v,
                                      };
                                    }
                                    return p;
                                  }),
                                );
                              }}
                            />
                          );
                        })}
                      </div>
                    );
                  }}
                />
                <div>
                  <Popover portal>
                    <Popover.Button minimal icon={Plus}>
                      {t('eleve:actions.addField')}
                    </Popover.Button>
                    <Popover.Panel overflow="visible" size="xl">
                      <div tw="p-4 max-h-[60vh] overflow-y-auto">
                        <AddNewFieldPopoverWrapper
                          viewId={viewId}
                          onClose={() => {
                            (buttonRef.current as any).click();
                          }}
                        />
                      </div>
                    </Popover.Panel>
                  </Popover>
                </div>
              </div>
            </CardLayout>
          </div>

          <div tw="mt-6">
            <DeleteButton
              portal={false}
              onClick={() => deleteEleve(eleve.id)}
              small
              icon={TrashIcon}
              withConfirmation
              minimal
            >
              {t('eleve:deleteEleve')}
            </DeleteButton>
          </div>
        </form>
      )}
    />
  );
}
